import { ArrowRight, LoaderCircle } from "lucide-react"
import { Link, useForm, usePage } from "@inertiajs/react"
import { FormEvent } from "react"

import { GoogleSignInButton } from "@/components/features/auth/google-sign-in-button"
import {
  EmailVerificationSteps,
  type VerificationStep,
} from "@/components/features/auth/email-verification-steps"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

const KEY = "ui.react.pages.staff.registrations.new"

// Staff::RegistrationsController renders `@user.errors.to_hash(true)`, so every
// field arrives as an array of full messages rather than a single string.
// Same shape, same helper as pages/staff/locations/new.tsx.
function errorMessage(value: string | string[] | undefined): string | null {
  if (!value) return null
  return Array.isArray(value) ? value[0] : value
}

interface RegistrationProps {
  step: VerificationStep
  emailAddress: string | null
  codeLength: number
  codeTtlMinutes: number
  errors?: Record<string, string | string[] | undefined>
  // `usePage` constrains its type parameter to Inertia's PageProps, which
  // carries an index signature for the shared props the layout injects.
  [key: string]: unknown
}

/**
 * Step 1 of 2. This form only creates the person; the restaurant is created on
 * the next screen, which is why the submit button names that destination
 * instead of saying "Registrarse".
 *
 * The email route runs address → code → name and password. Staff used to reach
 * an account with no verification at all, which was the larger of the two holes
 * rather than the smaller: behind a staff account sit a tenant and a Stripe
 * Connect account.
 *
 * There is no `email_address` field on the account step any more — the server
 * takes the address off the verified proof. `tests/staff/authentication.spec.ts`
 * selects on the remaining field ids (`name`, `password`,
 * `password_confirmation`) and on the error paragraph's class string.
 */
export function AccountForm({ className }: { className?: string }) {
  const {
    step,
    emailAddress,
    codeLength,
    codeTtlMinutes,
    errors: pageErrors = {},
  } = usePage<RegistrationProps>().props

  const { data, setData, post, errors, processing } = useForm({
    name: "",
    password: "",
    password_confirmation: "",
  })

  const formErrors = errors as Record<string, string | string[] | undefined>
  const nameError = errorMessage(formErrors.name)
  const passwordError = errorMessage(formErrors.password)
  const passwordConfirmationError = errorMessage(formErrors.password_confirmation)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    post("/registration")
  }

  return (
    <Card className={cn("w-full", className)} data-testid="staff-registration-card">
      <CardHeader>
        <CardTitle className="text-lg">{t(`${KEY}.card_title`)}</CardTitle>
        <CardDescription>{t(`${KEY}.card_description`)}</CardDescription>
      </CardHeader>

      <CardContent className="flex flex-col gap-4">
        {/* Only before the code round-trip starts — see the diner sign-up for
            the same reasoning. */}
        {step === "email" && (
          <>
            <GoogleSignInButton label={t(`${KEY}.continue_with_google`)} />

            <div className="flex items-center gap-3">
              <span className="h-px flex-1 bg-border" aria-hidden />
              <span className="text-xs text-muted-foreground">{t(`${KEY}.or_with_email`)}</span>
              <span className="h-px flex-1 bg-border" aria-hidden />
            </div>
          </>
        )}

        <EmailVerificationSteps
          step={step}
          emailAddress={emailAddress}
          codeLength={codeLength}
          codeTtlMinutes={codeTtlMinutes}
          errors={pageErrors}
          sendCodePath="/registration/send_code"
          verifyCodePath="/registration/verify_code"
          testIdPrefix="staff-registration"
        />

        {step === "details" && (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">{t(`${KEY}.name`)}</Label>
              <Input
                id="name"
                type="text"
                placeholder={t(`${KEY}.john_doe`)}
                value={data.name}
                onChange={(event) => setData("name", event.target.value)}
                aria-invalid={Boolean(nameError)}
                data-testid="staff-registration-name"
                required
              />
              {nameError && <p className="text-sm font-medium text-destructive">{nameError}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password">{t(`${KEY}.password`)}</Label>
              <Input
                id="password"
                type="password"
                value={data.password}
                onChange={(event) => setData("password", event.target.value)}
                aria-invalid={Boolean(passwordError)}
                data-testid="staff-registration-password"
                required
              />
              {passwordError && <p className="text-sm font-medium text-destructive">{passwordError}</p>}
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password_confirmation">{t(`${KEY}.confirm_password`)}</Label>
              <Input
                id="password_confirmation"
                type="password"
                value={data.password_confirmation}
                onChange={(event) => setData("password_confirmation", event.target.value)}
                aria-invalid={Boolean(passwordConfirmationError)}
                data-testid="staff-registration-password-confirmation"
                required
              />
              {passwordConfirmationError && (
                <p className="text-sm font-medium text-destructive">{passwordConfirmationError}</p>
              )}
            </div>

            <div className="flex flex-col gap-2 pt-1">
              <Button
                type="submit"
                className="w-full"
                disabled={processing}
                data-testid="staff-registration-submit"
              >
                {processing ? t(`${KEY}.submit_processing`) : t(`${KEY}.submit`)}
                {processing ? <LoaderCircle className="animate-spin" /> : <ArrowRight />}
              </Button>
              <p className="text-center text-xs leading-5 text-muted-foreground">
                {t(`${KEY}.next_hint`)}
              </p>
            </div>
          </form>
        )}

        <div className="text-center text-sm text-muted-foreground">
          {t(`${KEY}.already_tienes_bill`)}{" "}
          <Link
            href="/session/new"
            className="font-medium text-foreground underline underline-offset-4"
          >
            {t(`${KEY}.start_session`)}
          </Link>
        </div>
      </CardContent>
    </Card>
  )
}
